import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, Filter, RefreshCw, Plus, Eye, EyeOff, Shield, Crown, Briefcase, User
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role icons and colors
const ROLE_CONFIG = {
  admin: { icon: Shield, color: "text-red-400", bg: "bg-red-500/20" },
  director: { icon: Crown, color: "text-purple-400", bg: "bg-purple-500/20" },
  manager: { icon: Briefcase, color: "text-blue-400", bg: "bg-blue-500/20" },
  sales_rep: { icon: User, color: "text-green-400", bg: "bg-green-500/20" }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    active: "bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 text-xs",
    disabled: "bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 text-xs"
  };
  return <span className={styles[status] || styles.active}>{status}</span>;
};

// Role Badge Component
const RoleBadge = ({ role }) => {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.sales_rep;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${config.bg} ${config.color} border border-current/30`}>
      <Icon className="w-3 h-3" />
      {role.replace('_', ' ')}
    </span>
  );
};

export default function TeamManagement({ token, userRole }) {
  const [employees, setEmployees] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [newEmployee, setNewEmployee] = useState({ 
    name: "", email: "", phone: "", role: "sales_rep", password: "", manager_id: "" 
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [empRes, dirRes, mgrRes] = await Promise.all([
        axios.get(`${API}/employees${filterRole !== 'all' ? `?role=${filterRole}` : ''}`, { headers }),
        userRole === 'admin' ? axios.get(`${API}/employees/directors`, { headers }) : Promise.resolve({ data: { directors: [] } }),
        axios.get(`${API}/employees/managers`, { headers })
      ]);
      setEmployees(empRes.data.employees);
      setDirectors(dirRes.data.directors);
      setManagers(mgrRes.data.managers);
    } catch (error) {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterRole]);

  const createEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email || !newEmployee.password) {
      toast.error("Fill required fields");
      return;
    }
    try {
      await axios.post(`${API}/employees`, newEmployee, { headers });
      toast.success("Employee created");
      setNewEmployee({ name: "", email: "", phone: "", role: "sales_rep", password: "", manager_id: "" });
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await axios.put(`${API}/employees/${id}`, { status: currentStatus === 'active' ? 'disabled' : 'active' }, { headers });
      toast.success("Status updated");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const resetPassword = async (id) => {
    if (!window.confirm("Reset password to 'password123'?")) return;
    try {
      await axios.post(`${API}/employees/${id}/reset-password`, {}, { headers });
      toast.success("Password reset to: password123");
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="team-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[180px] input-dark" data-testid="role-filter">
              <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="director">Directors</SelectItem>
              <SelectItem value="manager">Managers</SelectItem>
              <SelectItem value="sales_rep">Sales Reps</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={fetchData} className="text-white/70" data-testid="refresh-team">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2" data-testid="add-employee-btn">
          <Plus className="w-4 h-4" /> Add Employee
        </Button>
      </div>

      {showForm && (
        <div className="card p-6 border border-[#D4AF37]/30" data-testid="employee-form">
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4">New Employee</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Name *</label>
              <Input 
                value={newEmployee.name} 
                onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})} 
                className="input-dark" 
                data-testid="employee-name"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Email *</label>
              <Input 
                type="email" 
                value={newEmployee.email} 
                onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} 
                className="input-dark"
                data-testid="employee-email"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Phone</label>
              <Input 
                value={newEmployee.phone} 
                onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})} 
                className="input-dark"
                data-testid="employee-phone"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Role</label>
              <Select value={newEmployee.role} onValueChange={(v) => setNewEmployee({...newEmployee, role: v})}>
                <SelectTrigger className="input-dark" data-testid="employee-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {userRole === 'admin' && <SelectItem value="director">Director</SelectItem>}
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newEmployee.role === 'sales_rep' && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Manager</label>
                <Select value={newEmployee.manager_id} onValueChange={(v) => setNewEmployee({...newEmployee, manager_id: v})}>
                  <SelectTrigger className="input-dark" data-testid="employee-manager">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="block text-white/70 text-sm mb-2">Password *</label>
              <Input 
                type="password" 
                value={newEmployee.password} 
                onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})} 
                className="input-dark"
                data-testid="employee-password"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={createEmployee} className="btn-primary" data-testid="create-employee-btn">Create</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-white/70">Cancel</Button>
          </div>
        </div>
      )}

      <div className="card border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="employee-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Manager</th>
                <th>Status</th>
                <th>Sales</th>
                <th>Commission</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-white/50">No employees found</td></tr>
              ) : (
                employees.map((emp) => (
                  <tr key={emp.id} data-testid={`employee-row-${emp.id}`}>
                    <td className="font-medium text-white">{emp.name}</td>
                    <td><RoleBadge role={emp.role} /></td>
                    <td className="text-white/70">{emp.email}</td>
                    <td className="text-white/50">{managers.find(m => m.id === emp.manager_id)?.name || '-'}</td>
                    <td><StatusBadge status={emp.status} /></td>
                    <td className="text-[#D4AF37]">{emp.total_sales || 0}</td>
                    <td className="text-green-400">${(emp.total_commission || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleStatus(emp.id, emp.status)} 
                          className="text-yellow-400" 
                          title="Toggle status"
                          data-testid={`toggle-status-${emp.id}`}
                        >
                          {emp.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        {userRole === 'admin' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => resetPassword(emp.id)} 
                            className="text-blue-400" 
                            title="Reset password"
                            data-testid={`reset-password-${emp.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export { RoleBadge, StatusBadge, ROLE_CONFIG };
